import { useState, useEffect } from 'react';
import { getAll, add, update, remove, STORES } from '../db';
import { Search, Plus, ChevronDown, ChevronUp, Trash2, Edit3, Save, X, Gift, AlertCircle, Pill, Leaf, FileText, CreditCard, Package } from 'lucide-react';

const SKIN_CONDITIONS = ['Acne', 'Rosacea', 'Pigmentation', 'Ageing', 'Sensitive', 'Dehydration', 'Scarring', 'Melasma', 'Eczema', 'Psoriasis', 'Thread Veins', 'Hair Loss', 'Sun Damage', 'Dull Skin', 'Large Pores'];

const emptyPatient = {
  firstName: '', lastName: '', dob: '', email: '', phone: '',
  address1: '', address2: '', town: '', county: '', postcode: '',
  skinConditions: [], allergies: '', medications: '', supplements: '',
  skinNotes: '', specialNotes: '', skinRecommendations: '',
  paymentLog: [], packageLog: [], prescriptions: [],
};

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyPatient);
  const [showNew, setShowNew] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => { load(); }, []);

  const load = async () => {
    const data = await getAll(STORES.patients);
    setPatients(data.sort((a, b) => a.lastName?.localeCompare(b.lastName)));
  };

  const filtered = patients.filter(p =>
    `${p.firstName} ${p.lastName} ${p.email} ${p.phone}`.toLowerCase().includes(search.toLowerCase())
  );

  const isBirthdayThisMonth = (dob) => {
    if (!dob) return false;
    const d = new Date(dob);
    return d.getMonth() === new Date().getMonth();
  };

  const age = (dob) => {
    if (!dob) return '';
    const d = new Date(dob);
    const age = Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24 * 365.25));
    return age;
  };

  const saveNew = async () => {
    if (!form.firstName || !form.lastName) return;
    await add(STORES.patients, form);
    setForm(emptyPatient);
    setShowNew(false);
    load();
  };

  const saveEdit = async () => {
    await update(STORES.patients, editing);
    setEditing(null);
    load();
  };

  const deletePatient = async (id) => {
    if (!window.confirm('Delete this patient?')) return;
    await remove(STORES.patients, id);
    setExpanded(null);
    load();
  };

  const toggleCondition = (cond, obj, setObj) => {
    const current = obj.skinConditions || [];
    setObj({ ...obj, skinConditions: current.includes(cond) ? current.filter(c => c !== cond) : [...current, cond] });
  };

  const PatientForm = ({ data, setData, onSave, onCancel }) => (
    <div className="patient-form">
      <div className="form-section-title">Personal Details</div>
      <div className="form-grid">
        <div className="form-field">
          <label>First Name *</label>
          <input value={data.firstName} onChange={e => setData({ ...data, firstName: e.target.value })} placeholder="First name" />
        </div>
        <div className="form-field">
          <label>Last Name *</label>
          <input value={data.lastName} onChange={e => setData({ ...data, lastName: e.target.value })} placeholder="Last name" />
        </div>
        <div className="form-field">
          <label>Date of Birth</label>
          <input type="date" value={data.dob} onChange={e => setData({ ...data, dob: e.target.value })} />
        </div>
        <div className="form-field">
          <label>Email</label>
          <input type="email" value={data.email} onChange={e => setData({ ...data, email: e.target.value })} placeholder="email@example.com" />
        </div>
        <div className="form-field">
          <label>Phone</label>
          <input value={data.phone} onChange={e => setData({ ...data, phone: e.target.value })} placeholder="07..." />
        </div>
        <div className="form-field">
          <label>Address Line 1</label>
          <input value={data.address1} onChange={e => setData({ ...data, address1: e.target.value })} />
        </div>
        <div className="form-field">
          <label>Address Line 2</label>
          <input value={data.address2} onChange={e => setData({ ...data, address2: e.target.value })} />
        </div>
        <div className="form-field">
          <label>Town</label>
          <input value={data.town} onChange={e => setData({ ...data, town: e.target.value })} />
        </div>
        <div className="form-field">
          <label>County</label>
          <input value={data.county} onChange={e => setData({ ...data, county: e.target.value })} />
        </div>
        <div className="form-field">
          <label>Postcode</label>
          <input value={data.postcode} onChange={e => setData({ ...data, postcode: e.target.value })} style={{ textTransform: 'uppercase' }} />
        </div>
      </div>

      <div className="form-section-title" style={{ marginTop: '1.5rem' }}>Skin Conditions</div>
      <div className="condition-pills">
        {SKIN_CONDITIONS.map(c => (
          <button key={c} className={`condition-pill ${(data.skinConditions || []).includes(c) ? 'active' : ''}`}
            onClick={() => toggleCondition(c, data, setData)}>{c}</button>
        ))}
      </div>

      <div className="form-section-title" style={{ marginTop: '1.5rem' }}>Clinical Notes</div>
      <div className="form-grid">
        <div className="form-field full">
          <label>Allergies</label>
          <input value={data.allergies} onChange={e => setData({ ...data, allergies: e.target.value })} placeholder="Known allergies..." />
        </div>
        <div className="form-field full">
          <label>Current Medications</label>
          <textarea value={data.medications} onChange={e => setData({ ...data, medications: e.target.value })} placeholder="Current medications..." rows={2} />
        </div>
        <div className="form-field full">
          <label>Supplements</label>
          <textarea value={data.supplements} onChange={e => setData({ ...data, supplements: e.target.value })} placeholder="Supplements, vitamins..." rows={2} />
        </div>
        <div className="form-field full">
          <label>Skin Notes</label>
          <textarea value={data.skinNotes} onChange={e => setData({ ...data, skinNotes: e.target.value })} placeholder="Skin assessment notes..." rows={3} />
        </div>
        <div className="form-field full">
          <label>Special Notes</label>
          <textarea value={data.specialNotes} onChange={e => setData({ ...data, specialNotes: e.target.value })} placeholder="Anything else to remember..." rows={2} />
        </div>
        <div className="form-field full">
          <label>Skincare Recommendations</label>
          <textarea value={data.skinRecommendations} onChange={e => setData({ ...data, skinRecommendations: e.target.value })} placeholder="Recommended products and routine..." rows={3} />
        </div>
      </div>

      <div className="form-actions">
        <button className="btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="btn-gold" onClick={onSave}><Save size={14} /> Save Patient</button>
      </div>
    </div>
  );

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Patients</h2>
          <p className="section-sub">{patients.length} patient{patients.length !== 1 ? 's' : ''} on record</p>
        </div>
        <button className="btn-gold" onClick={() => { setShowNew(true); setExpanded(null); }}>
          <Plus size={14} /> New Patient
        </button>
      </div>

      <div className="search-bar">
        <Search size={15} className="search-icon" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search patients..." className="search-input" />
      </div>

      {showNew && (
        <div className="patient-card expanded" style={{ marginBottom: '1rem' }}>
          <div className="card-header">
            <div className="patient-name-block">
              <div className="patient-initials">+</div>
              <div><div className="patient-name">New Patient</div></div>
            </div>
          </div>
          <PatientForm data={form} setData={setForm} onSave={saveNew} onCancel={() => { setShowNew(false); setForm(emptyPatient); }} />
        </div>
      )}

      <div className="patient-list">
        {filtered.map(p => (
          <div key={p.id} className={`patient-card ${expanded === p.id ? 'expanded' : ''}`}>
            <div className="card-header" onClick={() => {
              setExpanded(expanded === p.id ? null : p.id);
              setEditing(null);
              setActiveTab('details');
            }}>
              <div className="patient-name-block">
                <div className="patient-initials">{p.firstName?.[0]}{p.lastName?.[0]}</div>
                <div>
                  <div className="patient-name">
                    {p.firstName} {p.lastName}
                    {isBirthdayThisMonth(p.dob) && <span className="birthday-badge"><Gift size={11} /> Birthday this month!</span>}
                  </div>
                  <div className="patient-meta">
                    {p.dob && <span>{age(p.dob)} yrs</span>}
                    {p.email && <span>{p.email}</span>}
                    {p.phone && <span>{p.phone}</span>}
                    {(p.skinConditions || []).slice(0, 3).map(c => <span key={c} className="mini-tag">{c}</span>)}
                  </div>
                </div>
              </div>
              <div className="card-actions" onClick={e => e.stopPropagation()}>
                <button className="icon-btn" onClick={() => { setEditing(p); setExpanded(p.id); setActiveTab('details'); }}><Edit3 size={14} /></button>
                <button className="icon-btn danger" onClick={() => deletePatient(p.id)}><Trash2 size={14} /></button>
                <span className="expand-icon">{expanded === p.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
              </div>
            </div>

            {expanded === p.id && (
              <div className="card-body">
                {editing?.id === p.id ? (
                  <PatientForm data={editing} setData={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} />
                ) : (
                  <>
                    <div className="patient-tabs">
                      {[
                        { id: 'details', label: 'Details', icon: FileText },
                        { id: 'clinical', label: 'Clinical', icon: AlertCircle },
                        { id: 'skin', label: 'Skin', icon: Leaf },
                        { id: 'payments', label: 'Payments', icon: CreditCard },
                        { id: 'packages', label: 'Packages', icon: Package },
                      ].map(tab => (
                        <button key={tab.id} className={`patient-tab ${activeTab === tab.id ? 'active' : ''}`}
                          onClick={() => setActiveTab(tab.id)}>
                          <tab.icon size={12} /> {tab.label}
                        </button>
                      ))}
                    </div>

                    {activeTab === 'details' && (
                      <div className="detail-grid">
                        <div className="detail-row"><span className="detail-label">Full Name</span><span>{p.firstName} {p.lastName}</span></div>
                        <div className="detail-row"><span className="detail-label">Date of Birth</span><span>{p.dob ? new Date(p.dob).toLocaleDateString('en-GB') : '—'} {p.dob && `(${age(p.dob)} yrs)`}</span></div>
                        <div className="detail-row"><span className="detail-label">Email</span><span>{p.email || '—'}</span></div>
                        <div className="detail-row"><span className="detail-label">Phone</span><span>{p.phone || '—'}</span></div>
                        <div className="detail-row full"><span className="detail-label">Address</span>
                          <span>{[p.address1, p.address2, p.town, p.county, p.postcode].filter(Boolean).join(', ') || '—'}</span>
                        </div>
                      </div>
                    )}

                    {activeTab === 'clinical' && (
                      <div className="detail-grid">
                        <div className="detail-row full"><span className="detail-label"><AlertCircle size={12} /> Allergies</span><span className={p.allergies ? 'allergy-text' : ''}>{p.allergies || 'None recorded'}</span></div>
                        <div className="detail-row full"><span className="detail-label"><Pill size={12} /> Medications</span><span>{p.medications || 'None recorded'}</span></div>
                        <div className="detail-row full"><span className="detail-label"><Leaf size={12} /> Supplements</span><span>{p.supplements || 'None recorded'}</span></div>
                        <div className="detail-row full"><span className="detail-label">Special Notes</span><span>{p.specialNotes || '—'}</span></div>
                      </div>
                    )}

                    {activeTab === 'skin' && (
                      <div className="detail-grid">
                        <div className="detail-row full"><span className="detail-label">Skin Conditions</span>
                          <div className="condition-pills" style={{ marginTop: '4px' }}>
                            {(p.skinConditions || []).length > 0
                              ? p.skinConditions.map(c => <span key={c} className="condition-pill active small">{c}</span>)
                              : <span>None recorded</span>}
                          </div>
                        </div>
                        <div className="detail-row full"><span className="detail-label">Skin Notes</span><span>{p.skinNotes || '—'}</span></div>
                        <div className="detail-row full"><span className="detail-label">Recommendations</span><span>{p.skinRecommendations || '—'}</span></div>
                      </div>
                    )}

                    {activeTab === 'payments' && (
                      <div className="log-section">
                        <p className="log-empty">Payment log coming soon — will link to treatments and packages.</p>
                      </div>
                    )}

                    {activeTab === 'packages' && (
                      <div className="log-section">
                        <p className="log-empty">Package log coming soon — will link to packages builder.</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        ))}

        {filtered.length === 0 && !showNew && (
          <div className="empty-state">
            <p>No patients found</p>
            <button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add your first patient</button>
          </div>
        )}
      </div>
    </div>
  );
}
